// @flow
import React from 'react';
import type { ChildrenArray, Element } from 'react';
import {
  types as t,
  usePropsValidation,
} from '../validation/runtime-checks/index.js';
import color from '../color.js';
import { Heading } from '../Heading.js';
import { Text } from '../Text.js';
import { productColor, illustrationColor } from '../designSystem.js';
import { CollapsibleInternal } from '../CollapsibleInternal.js';

const CollapseChevron = ({ isOpen }) => (
  <svg
    width="12"
    height="7.5"
    viewBox="0 0 8 5"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={{ transform: isOpen ? `rotate(180deg)` : undefined }}
  >
    <path
      d="M3.60957 4.51196L0.649878 0.812348C0.387973 0.484966 0.621059 0 1.04031 0H6.95969C7.37894 0 7.61203 0.484966 7.35012 0.812348L4.39043 4.51196C4.19027 4.76216 3.80973 4.76216 3.60957 4.51196Z"
      fill={illustrationColor.cloudburst}
    />
  </svg>
);

export function TSOStandardsAndBenchmarksSection(props: {|
  children: $ReadOnlyArray<
    | Element<typeof TSOFloridaMTRStandardsList>
    | ?Element<typeof TSOFloridaBenchmarksList>
    | ?Element<typeof TSOFloridaConnectionsList>
    | ?Element<typeof TSOFloridaAccessPointsList>,
  >,
|}) {
  const { failedValidation } = usePropsValidation(
    props,
    TSOStandardsAndBenchmarksSection,
  );

  if (failedValidation) return null;

  return (
    <div style={{ color: productColor.shuttleGrey, paddingBottom: 40 }}>
      <Heading>Standards and benchmarks</Heading>
      {props.children}
    </div>
  );
}

TSOStandardsAndBenchmarksSection.spec = {
  children: t.exactOrderChildrenArray(
    t.elementType(TSOFloridaMTRStandardsList),
    t.optional(t.elementType(TSOFloridaBenchmarksList)),
    t.optional(t.elementType(TSOFloridaConnectionsList)),
    t.optional(t.elementType(TSOFloridaAccessPointsList)),
  ),
};

function TSOFloridaStandardAndBenchmarkCollapsableInternal(props: {|
  children: $ReadOnlyArray<Element<*>>,
  title: string,
  icon?: Element<*>,
  description: string,
  openByDefault: boolean,
|}) {
  return (
    <div
      style={{
        backgroundColor: productColor.seashell,
        padding: 20,
        paddingTop: 1,
        paddingBottom: 1,
        borderRadius: 8,
        marginBottom: 20,
      }}
    >
      <CollapsibleInternal
        icon={
          <div style={{ display: 'flex', alignItems: 'center' }}>
            {props.icon}
            <div style={{ width: 16 }} />
          </div>
        } // CollapsibleInternal doesnt's add right spacing to the icon
        hideHorizontalRule
        openByDefault={props.openByDefault}
        title={props.title}
        customChevron={CollapseChevron}
      >
        <p
          style={{
            fontWeight: 400,
            color: color.shuttleGray,
            fontSize: 16,
            lineHeight: '22px',
            marginBottom: 16,
          }}
        >
          {props.description}
        </p>
        <div style={{ paddingLeft: 30 }}>{props.children}</div>
      </CollapsibleInternal>
    </div>
  );
}

export function TSOFloridaMTRStandardsList(props: {|
  children: $ReadOnlyArray<Element<typeof TSOFloridaMTRStandard>>,
|}) {
  const { failedValidation } = usePropsValidation(
    props,
    TSOFloridaMTRStandardsList,
  );

  if (failedValidation) return null;

  return (
    <TSOFloridaStandardAndBenchmarkCollapsableInternal
      openByDefault
      title="Math Thinking and Reasoning standards"
      description={`This subtopic provides opportunities to integrate the following Math
    Thinking and Reasoning standard${props.children.length > 1 ? 's' : ''}:`}
    >
      {props.children}
    </TSOFloridaStandardAndBenchmarkCollapsableInternal>
  );
}

TSOFloridaMTRStandardsList.spec = {
  children: t.minimumCount(1, t.elementType(TSOFloridaMTRStandard)),
};

// there are precisely seven MTR standards in the Florida BEST standards. Authors
// will cite these by number, and the code and description will be automatically
// generated
const MTR_STANDARDS_DESCRIPTION = [
  'Enter the MTR as a number between 1 and 7 in the number prop',
  'Actively participate in effortful learning both individually and collectively',
  'Demonstrate understanding by representing problems in multiple ways',
  'Complete tasks with mathematical fluency',
  'Engage in discussions that reflect on the mathematical thinking of self and others',
  'Use patterns and structure to help understand and connect mathematical concepts',
  'Assess the reasonableness of solutions',
  'Apply mathematics to real-world contexts',
];

export function TSOFloridaMTRStandard(props: {|
  number: number,
  children: ChildrenArray<Element<typeof Text>>,
|}) {
  const { failedValidation } = usePropsValidation(props, TSOFloridaMTRStandard);

  if (failedValidation) return null;

  return (
    <div
      style={{
        fontFamily: 'Proxima Nova',
        marginTop: 20,
      }}
    >
      <h4
        style={{
          fontWeight: 600,
          color: color.cloudBurst,
          fontSize: 16,
          lineHeight: '22px',
          marginTop: 4,
          marginBottom: 4,
        }}
      >
        (MA.K12.MTR.{props.number}.1) {MTR_STANDARDS_DESCRIPTION[props.number]}
      </h4>
      <p
        style={{
          fontWeight: 400,
          color: color.shuttleGray,
          fontSize: 16,
          lineHeight: '22px',
          marginTop: 0,
          marginBottom: 8,
        }}
      >
        {props.children}
      </p>
    </div>
  );
}

TSOFloridaMTRStandard.spec = {
  number: t.unionOfValues(0, 1, 2, 3, 4, 5, 6, 7),
  children: t.elementType(Text),
};

export function TSOFloridaBenchmarksList(props: {|
  children: $ReadOnlyArray<Element<typeof TSOFloridaBenchmark>>,
|}) {
  const { failedValidation } = usePropsValidation(
    props,
    TSOFloridaBenchmarksList,
  );

  if (failedValidation) return null;

  return (
    <TSOFloridaStandardAndBenchmarkCollapsableInternal
      openByDefault
      title="Florida benchmarks"
      description={`This subtopic addresses the following Florida B.E.S.T. benchmark${
        props.children.length > 1 ? 's' : ''
      }:`}
    >
      {props.children}
    </TSOFloridaStandardAndBenchmarkCollapsableInternal>
  );
}

TSOFloridaBenchmarksList.spec = {
  children: t.minimumCount(1, t.elementType(TSOFloridaBenchmark)),
};

export function TSOFloridaConnectionsList(props: {|
  children: $ReadOnlyArray<Element<typeof TSOFloridaBenchmark>>,
|}) {
  const { failedValidation } = usePropsValidation(
    props,
    TSOFloridaBenchmarksList,
  );

  if (failedValidation) return null;

  return (
    <TSOFloridaStandardAndBenchmarkCollapsableInternal
      openByDefault={false}
      title="Connecting benchmarks"
      description=""
    >
      {props.children}
    </TSOFloridaStandardAndBenchmarkCollapsableInternal>
  );
}

TSOFloridaConnectionsList.spec = {
  children: t.minimumCount(1, t.elementType(TSOFloridaBenchmark)),
};

export function TSOFloridaAccessPointsList(props: {|
  children: $ReadOnlyArray<Element<typeof TSOFloridaAccessPoint>>,
|}) {
  const { failedValidation } = usePropsValidation(
    props,
    TSOFloridaAccessPointsList,
  );

  if (failedValidation) return null;

  return (
    <TSOFloridaStandardAndBenchmarkCollapsableInternal
      openByDefault={false}
      title="Access points"
      icon={<FLAccessPointIcon />}
      description={`This subtopic addresses the following Florida Access Point${
        props.children.length > 1 ? 's' : ''
      }:`}
    >
      {props.children}
    </TSOFloridaStandardAndBenchmarkCollapsableInternal>
  );
}

TSOFloridaAccessPointsList.spec = {
  children: t.minimumCount(1, t.elementType(TSOFloridaAccessPoint)),
};

export function TSOFloridaAccessPoint(props: {| code: string, children: * |}) {
  const { failedValidation } = usePropsValidation(props, TSOFloridaAccessPoint);

  if (failedValidation) return null;

  return (
    <div
      style={{
        fontFamily: 'Proxima Nova',
        marginTop: 20,
      }}
    >
      <h4
        style={{
          fontWeight: 600,
          color: color.cloudBurst,
          fontSize: 16,
          lineHeight: '22px',
          marginTop: 4,
          marginBottom: 4,
        }}
      >
        {props.code}
      </h4>
      <p
        style={{
          fontWeight: 400,
          color: color.shuttleGray,
          fontSize: 16,
          lineHeight: '22px',
          marginTop: 0,
          marginBottom: 8,
        }}
      >
        {props.children}
      </p>
    </div>
  );
}

TSOFloridaAccessPoint.spec = {
  code: t.string,
  children: Text.spec.children,
};

export function TSOFloridaBenchmark(props: {|
  code: string,
  children: *,
  FLIcon?: 'honors' | 'accessPoint',
|}) {
  const { failedValidation } = usePropsValidation(props, TSOFloridaBenchmark);

  if (failedValidation) return null;

  return (
    <div
      style={{
        fontFamily: 'Proxima Nova',
        marginTop: 20,
      }}
    >
      <header>
        {props.FLIcon != null && (
          <div style={{ marginLeft: -30, position: 'absolute' }}>
            {props.FLIcon === 'honors' && <FLHonorsIcon />}
            {props.FLIcon === 'accessPoint' && <FLAccessPointIcon />}
          </div>
        )}
        <h4
          style={{
            fontWeight: 600,
            color: color.cloudBurst,
            fontSize: 16,
            lineHeight: '22px',
            marginTop: 4,
            marginBottom: 4,
          }}
        >
          {props.code}
        </h4>
      </header>
      <p
        style={{
          fontWeight: 400,
          color: color.shuttleGray,
          fontSize: 16,
          lineHeight: '22px',
          marginTop: 0,
          marginBottom: 8,
        }}
      >
        {props.children}
      </p>
    </div>
  );
}

TSOFloridaBenchmark.spec = {
  code: t.string,
  children: Text.spec.children,
  FLIcon: t.optional(t.unionOfValues('honors', 'accessPoint')),
};

function FLHonorsIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 18 18"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <circle cx="9" cy="9" r="9" fill="#21374D" />
      <path
        d="M6.76309 6V8.29714H5.01867V6H3.75V12H5.01867V9.61714H6.76309V12H8.03176V6H6.76309Z"
        fill="white"
      />
      <path
        d="M12.9813 6V9.25714L10.8405 6H9.88896V12H11.1576V8.74286L13.2985 12H14.25V6H12.9813Z"
        fill="white"
      />
    </svg>
  );
}

function FLAccessPointIcon() {
  return (
    <svg
      width="19"
      height="18"
      viewBox="0 0 19 18"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <circle cx="9.75" cy="9" r="9" fill="#21374D" />
      <path
        d="M8.69583 11.8125H10.0849L8.19578 6.1875H6.57657L4.6875 11.8125H6.07652L6.35433 10.9286H8.41802L8.69583 11.8125ZM6.73532 9.72321L7.38618 7.65804L8.03703 9.72321H6.73532Z"
        fill="white"
      />
      <path
        d="M12.8679 6.1875H10.7645V11.8125H12.0344V10.0446H12.8679C13.9632 10.0446 14.8125 9.18482 14.8125 8.11607C14.8125 7.04732 13.9632 6.1875 12.8679 6.1875ZM12.8679 8.83929H12.0344V7.39286H12.8679C13.2568 7.39286 13.5425 7.70625 13.5425 8.11607C13.5425 8.52589 13.2568 8.83929 12.8679 8.83929Z"
        fill="white"
      />
    </svg>
  );
}
